import {
  Chat,
  defaultMessageReactionCounts,
  MaybeOptimisticChatTextMessage,
  MessageReactionCounts,
  ReactionKey,
} from "@lazarentertainment/public-chat-web"
import SendArrow from "assets/icons/sendArrow.svg"
import { useChannelContext } from "client/components/channel"
import { ChatMessage, ChatText } from "client/designSystem/ChatMessage"
import { useVideoContext } from "client/packages/player/player/VideoContext"
import { MouseEvent, useEffect, useRef, useState } from "react"
import { FormattedMessage } from "react-intl"
import { AccentButton } from "shared/components/buttons"
import { StandardButton } from "shared/components/buttons/sendButton"
import { AspectRatio } from "shared/constants/views"
import useCurrentKeys from "shared/hooks/useCurrentKeys"
import styled, { css } from "styled-components"

import { ChatView } from "../constants"

import { ChatMessageContainer } from "./chat-message/ChatMessageContainer"
import {
  ChatNotification as ChatNotificationType,
  isChatNotification,
  useChatContext,
} from "./ChatContext"
import { ChatNotificationContainer } from "./ChatMessageStyles"

const HistoryContainer = styled.div<{
  $view: ChatView
  $subtitlesVisible?: boolean
  $aspectRatio?: AspectRatio
}>`
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0;
  position: relative;

  ${(props) =>
    props.$view === ChatView.OVERLAY &&
    css`
      mask-image: linear-gradient(to bottom, transparent 0px, black 30%);
      overflow: hidden; // flexbox magic: prevents chat from exceeding available height of parent
      max-height: 300px;
    `}
`

const HistoryScrollContainer = styled.div<{ $view: ChatView }>`
  overflow-y: auto;
  overflow-x: hidden;

  ${(props) =>
    props.$view === ChatView.OVERLAY &&
    css`
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    `}
`

const History = styled.ol<{ $showInput?: boolean; $view: ChatView }>`
  display: flex;
  flex-direction: column;
  margin-block-start: 0;
  margin-block-end: 0;
  margin-inline-start: 0;
  margin-inline-end: 0;
  padding-inline-start: 0;
  width: 100%;
  list-style: none;
  gap: var(--gap-md);
  padding: var(--padding-md);

  ${(props) =>
    props.$view === ChatView.OVERLAY &&
    css`
      gap: var(--gap-3xs);
      padding: var(--padding-sm);
    `}

  > :first-child {
    // HACK: A margin has been added to the top of the first child in order
    // to prevent clipping of the chat reactions menu (due to overflow: hidden
    // of the the parent container)
    margin-top: ${(props) => (props.$showInput ? "50px" : "8px")};
  }
`

const UnpauseButton = styled(AccentButton)`
  position: absolute;
  bottom: 10px;

  //horizontally center
  margin-left: 50%;
  transform: translateX(-50%);

  font-size: 0.8rem;
  height: 30px;
`

const MobileUnpauseButton = styled(StandardButton).attrs({
  children: <SendArrow />,
  type: "button",
})`
  position: absolute;
  // This is a bit of a hack to center the button in the full view
  // rather than having it centered in the chat column itself. Ideally we
  // would just used fixed positioning but there seems to be a bug in Chrome
  // where layers are not rendered correctly with a child that is fixed.
  // The result is that the chat container displays as an opaque black square
  // when the MobileUnpauseButton is fixed.
  left: 50%;

  svg {
    transform: rotate(180deg);
  }
`

const SCROLL_BOTTOM_TOLERANCE = 5

type ChatHistoryProps = {
  uiView: ChatView
  messages: Array<ChatNotificationType | MaybeOptimisticChatTextMessage>
  messageReactions: Record<string, MessageReactionCounts>
  showModerationTools?: boolean
  submitMessageReaction: (
    reactionKey: ReactionKey,
    messageTimetoken: bigint,
  ) => void
  banUser: Chat["banUser"]
  deleteChatMessage: Chat["deleteMessage"]
}

const ChatHistory = ({
  uiView,
  messages,
  messageReactions,
  showModerationTools,
  submitMessageReaction,
  banUser,
  deleteChatMessage,
}: ChatHistoryProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [scrollPaused, setScrollPaused] = useState(false)
  const currentKeys = useCurrentKeys()
  const isFocused = useRef(false)
  const isScrollBarClicked = useRef(false)
  const isTouchScrolling = useRef(false)

  const { subtitlesVisible } = useVideoContext()

  const { aspectRatio } = useChannelContext()

  const { isReplay } = useChatContext()

  const scrollToBottom = () => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight
    }
  }

  useEffect(() => {
    if (!scrollPaused) {
      scrollToBottom()
    }

    // If we sent the last message, unpause chat autoscroll
    const lastMessage = messages.length
      ? messages[messages.length - 1]
      : null

    if (
      // Don't unpause and auto-scroll chat for VOD, since the user can't send messages anyway.
      !isReplay &&
      lastMessage &&
      !isChatNotification(lastMessage) &&
      lastMessage.isMe
    ) {
      setScrollPaused(false)
      scrollToBottom()
    }
  }, [messages])

  const handleScroll = () => {
    const containerHeight =
      scrollContainerRef.current.scrollHeight - SCROLL_BOTTOM_TOLERANCE
    const scrollBottom =
      scrollContainerRef.current.scrollTop +
      scrollContainerRef.current.clientHeight
    // Reached bottom
    if (scrollPaused && scrollBottom >= containerHeight) {
      setScrollPaused(false)
    }

    // Simply trying to pause by detecting scrolling up via scroll events
    // has proven to be extremely error prone. The problem is that a lot
    // of stuff can cause scrolling but we only want to pause on scrolling
    // up via a user-initiated event. To solve the problem we're ignoring
    // scroll events and targeting specific user-driven events to determine
    // when a pause is necessary. This eliminates all of the buggy pausing
    // that we were seeing when scrolling was being triggered by high volumes
    // of incoming messages.
    //
    // Obviously the one catch is that if there is a way to scroll up that
    // we are not accounting for (different shortcut keys?) then the user
    // will be able to scroll up without a pause. This seems like a very
    // good tradeoff to fix the bugs we were getting with the other approach
    // If a new scroll trigger is identified though, it will have to get
    // added to the handlers below.
    if (
      !scrollPaused &&
      scrollBottom < containerHeight &&
      (isScrollBarClicked.current ||
        isTouchScrolling.current ||
        (isFocused.current &&
          (currentKeys.key === "ArrowUp" ||
            currentKeys.key === "PageUp" ||
            (currentKeys.key === " " && currentKeys.shift))))
    ) {
      setScrollPaused(true)
    }
  }

  // If the user is scrolling with the wheel & we're not at the bottom, pause
  const handleWheel = () => {
    const containerHeight =
      scrollContainerRef.current.scrollHeight - SCROLL_BOTTOM_TOLERANCE
    const scrollBottom =
      scrollContainerRef.current.scrollTop +
      scrollContainerRef.current.clientHeight
    if (!scrollPaused && scrollBottom < containerHeight) {
      setScrollPaused(true)
    }
  }

  // if the mouse clicks HistoryScrollContainer (but not the actual ChatHistory),
  // then the scroll bar was clicked.
  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === scrollContainerRef.current) {
      isScrollBarClicked.current = true
    }
  }

  const handleUnpauseClick = () => {
    setScrollPaused(false)
    scrollContainerRef.current.scrollTop =
      scrollContainerRef.current.scrollHeight
  }

  return (
    <HistoryContainer
      $view={uiView}
      $aspectRatio={aspectRatio}
      $subtitlesVisible={subtitlesVisible}
    >
      <HistoryScrollContainer
        tabIndex={0} // So that onFocus will work
        $view={uiView}
        ref={scrollContainerRef}
        onScroll={handleScroll}
        onWheel={handleWheel}
        onFocus={() => {
          isFocused.current = true
        }}
        onBlur={() => {
          isFocused.current = false
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={() => {
          isScrollBarClicked.current = false
        }}
        onTouchStart={() => {
          isTouchScrolling.current = true
        }}
        onTouchEnd={() => {
          isTouchScrolling.current = false
        }}
      >
        <History $showInput={!isReplay} $view={uiView}>
          {messages.map((message) => {
            if (isChatNotification(message)) {
              return (
                <ChatNotificationContainer
                  key={message.key}
                  data-view={uiView}
                >
                  <ChatMessage
                    variant={
                      message.highlight
                        ? "rose"
                        : uiView === ChatView.OVERLAY
                          ? "overlay-primary"
                          : "text"
                    }
                    icon={message.icon}
                  >
                    <ChatText text={message.text} />
                  </ChatMessage>
                </ChatNotificationContainer>
              )
            }

            return (
              <ChatMessageContainer
                key={message.messageKey}
                message={message}
                reactionCounts={
                  message.timetoken
                    ? messageReactions[message.timetoken.toString()]
                    : defaultMessageReactionCounts
                }
                submitMessageReaction={submitMessageReaction}
                showModerationTools={showModerationTools === true}
                banUser={banUser}
                view={uiView}
                deleteChatMessage={deleteChatMessage}
              />
            )
          })}
        </History>
      </HistoryScrollContainer>
      {scrollPaused && uiView === ChatView.DEFAULT && (
        <UnpauseButton type="button" onClick={handleUnpauseClick}>
          <FormattedMessage
            id="chatHistory.unpause"
            defaultMessage="Jump to Bottom"
          />
        </UnpauseButton>
      )}
      {scrollPaused && uiView === ChatView.OVERLAY && (
        <MobileUnpauseButton onClick={handleUnpauseClick} />
      )}
    </HistoryContainer>
  )
}

export default ChatHistory
